"use client";

import { useTenancy } from "@/lib/tenancy/tenancy-context";
import { useParams } from "@/lib/router/navigation";
import type { Permission } from "./permissions";

/**
 * Whether to SHOW something, which is not the same as whether it is allowed:
 * the platform authorises every request at the gateway, whatever this says.
 *
 * 2.795, SPONSOR RULING (2026-10-02, relayed by the coordinator): the console
 * hides or disables a control the signed-in person cannot use AS THE PLATFORM
 * REPORTS — from the platform's own answer about the caller
 * (`GET /v1/people/{sub}/permissions`, S-709, ADR-106), mapped to these names in
 * `platform-gate.ts`. Until that answer is read and parsed, and whenever the read
 * FAILS (a 503, a network error, a malformed answer, a 404 for oneself), this
 * answers YES and lets the gateway refuse: a control hidden from someone who MAY
 * use it is indistinguishable from a feature the product does not have, and a
 * failed read is never evidence that they may not. A permission the answer
 * cannot decide is always shown.
 *
 * A project capability is decided on the project in the route when there is
 * one, and on "any project" when there is not (a page spanning projects).
 */
export function usePermission(permission: Permission): boolean {
  const { can } = useTenancy();
  const { projectId } = useParams<{ projectId?: string }>();
  // A context with no decider (a partial test double) decides nothing: shown, as before.
  return typeof can === "function" ? can(permission, projectId) : true;
}

export function useHasAnyPermission(perms: Permission[]): boolean {
  const { can } = useTenancy();
  const { projectId } = useParams<{ projectId?: string }>();
  return typeof can === "function" ? perms.some((p) => can(p, projectId)) : true;
}

/**
 * Why a control is unavailable, in the platform's terms, or undefined when it
 * is available or the console does not know (see `whyNot` in the tenancy context).
 */
export function useDeniedReason(permission: Permission): string | undefined {
  const { whyNot } = useTenancy();
  const { projectId } = useParams<{ projectId?: string }>();
  return typeof whyNot === "function" ? whyNot(permission, projectId) : undefined;
}
